'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
		/**
		 * @deprecated Ignored. A tooltip has one look and it has no caret; the prop stays only so
		 * existing call sites keep compiling. Delete it when you touch one.
		 */
		withArrow?: boolean;
		/**
		 * `inverse` is the solid black tooltip (the report toolbar's). It has no arrow and no
		 * collision padding of its own; pass `collisionPadding` if the caller needs one.
		 */
		variant?: 'default' | 'inverse';
		/** @deprecated Ignored, see `withArrow`. */
		arrowAlign?: 'start' | 'center' | 'center28' | 'end';
	}
>(
	(
		{
			className,
			sideOffset = 4,
			/**
			 * A tooltip never touches the window edge.
			 *
			 * Radix's own default is 0, so a tooltip on a control at the end of a row - the
			 * account avatar in the header, a column tooltip at the right of a wide table - is
			 * pushed by collision detection until it is flush against the viewport, which reads
			 * as the layer having been cut off rather than placed. 16 is the gutter ladder's
			 * floor (`PAGE_GUTTER`'s `px-4`), so the layer keeps at least the page's own margin
			 * whatever it is anchored to.
			 */
			collisionPadding = 16,
			// Pulled out so they are not forwarded to the DOM.
			withArrow: _withArrow,
			arrowAlign: _arrowAlign,
			variant = 'default',
			...props
		},
		ref,
	) => (
		// Portalled, so the layer is placed against the viewport. In place it sat inside whatever
		// opened it, and under a `container-type` ancestor (the HumAIn creation panel) the
		// positioning library subtracted that ancestor's origin while the browser did not, which
		// put the tooltip a panel's width away from its trigger. A fullscreen element is the one
		// thing drawn above the body, so a tooltip opened inside one has to mount inside it.
		<TooltipPrimitive.Portal
			container={typeof document === 'undefined' ? undefined : (document.fullscreenElement ?? undefined)}>
			<TooltipPrimitive.Content
				side="top"
				align="center"
				ref={ref}
				sideOffset={sideOffset}
				collisionPadding={collisionPadding}
				className={cn(
					variant === 'inverse' && [
						'z-50 overflow-visible rounded-md bg-black text-white px-3 py-1.5 text-xs max-w-xs',
						'shadow-md relative trans',
					],
					variant === 'default' && [
						// A tooltip is a layer above the page, so it is a popover surface - a black
						// rectangle is the one element that never belonged to either theme.
						// The typography resets date from when the content rendered in place and inherited
						// `TableCell`'s `whitespace-nowrap` and `TableHead`'s `uppercase tracking-caps`.
						// They still hold for a tooltip mounted inside a fullscreen element.
						'z-50 max-w-xs whitespace-normal break-words normal-case tracking-normal overflow-visible rounded-sm border border-border bg-surface-raised px-3 py-1.5 text-xs text-foreground',
						'relative shadow-popover trans',
					],
					'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
					'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
					className,
				)}
				{...props}>
				{props.children}
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
